/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useState } from 'react';
import { TbSearch } from 'react-icons/tb';

import { RebrandShell } from 'components/rebrand/RebrandShell';
import { buildGallery, GalleryEntry, PersonSeedCard } from 'components/rebrand/recognized-gallery';
import { useAuth } from 'context/AuthContext';
import { getRewardFeed } from 'services/dashboard.service';

import './styles.scss';

type SortMode = 'recentes' | 'top' | 'az';

const PER_PAGE = 8;
const FEED_SIZE = 999;

function matchQuery(entry: GalleryEntry, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return [entry.name, entry.nomeGuerra, entry.playerId, entry.funcao, entry.unidade]
    .filter(Boolean)
    .some(field => String(field).toLowerCase().includes(q));
}

export default function GaleriaReconhecimento() {
  const { user, currentCycle } = useAuth();
  const [gallery, setGallery] = useState<GalleryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState<SortMode>('recentes');
  const [page, setPage] = useState(0);

  useEffect(() => {
    async function loadFeed() {
      setLoading(true);
      try {
        const data = await getRewardFeed(currentCycle?.date_start, currentCycle?.date_finish, 0, FEED_SIZE);
        setGallery(buildGallery(data?.result || []));
      } catch (e) {
        setGallery([]);
      }
      setLoading(false);
    }

    if (!!user && !!currentCycle) {
      loadFeed();
    }
  }, [user, currentCycle]);

  const filtered = useMemo(() => {
    const arr = gallery.filter(entry => matchQuery(entry, query));
    if (sort === 'top') return [...arr].sort((a, b) => b.total - a.total || b.lastAt - a.lastAt);
    if (sort === 'az') return [...arr].sort((a, b) => a.name.localeCompare(b.name, 'pt'));
    return arr; // 'recentes' — buildGallery já ordena por último recebimento
  }, [gallery, query, sort]);

  useEffect(() => {
    setPage(0);
  }, [query, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
  const pageItems = filtered.slice(page * PER_PAGE, (page + 1) * PER_PAGE);

  return (
    <RebrandShell
      title="Galeria do Reconhecimento"
      subtitle="Conheça as pessoas que foram reconhecidas e a cor de cada dimensão."
    >
      <div className="rb-gal-toolbar">
        <h2 className="rb-gal-toolbar__title">Todos os reconhecidos</h2>

        <div className="rb-gal-toolbar__controls">
          <div className="rb-gal-search">
            <span className="rb-gal-search__icon">
              <TbSearch size={16} />
            </span>
            <input
              className="rb-input rb-input--search"
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Nome, CPF ou unidade"
              inputMode="search"
            />
          </div>

          <select className="rb-input rb-gal-sort" value={sort} onChange={e => setSort(e.target.value as SortMode)}>
            <option value="recentes">Recentes</option>
            <option value="top">Mais reconhecidos</option>
            <option value="az">A–Z</option>
          </select>
        </div>
      </div>

      {!loading && (
        <div className="rb-gal-count">
          {filtered.length} {filtered.length === 1 ? 'pessoa encontrada' : 'pessoas encontradas'}
          {query ? ` para “${query}”` : ''}
        </div>
      )}

      {loading && (
        <div className="rb-hero-grid">
          {[0, 1, 2, 3].map(i => (
            <div key={i} className="rb-skeleton" style={{ height: 300 }} />
          ))}
        </div>
      )}

      {!loading && filtered.length === 0 && (
        <div className="rb-gal-empty">
          <div className="rb-gal-empty__icon">
            <TbSearch size={22} />
          </div>
          <p>
            {gallery.length === 0
              ? 'Ainda não há reconhecimentos neste ciclo. Seja o primeiro a reconhecer um colega.'
              : 'Nenhuma pessoa encontrada. Tente outro nome, CPF ou unidade.'}
          </p>
        </div>
      )}

      {!loading && filtered.length > 0 && (
        <div className="rb-hero-grid">
          {pageItems.map((entry, i) => (
            <PersonSeedCard
              key={entry.playerId}
              entry={entry}
              index={i}
              rank={sort === 'top' ? page * PER_PAGE + i + 1 : null}
            />
          ))}
        </div>
      )}

      {!loading && totalPages > 1 && (
        <div className="rb-gal-pager">
          <button type="button" className="rb-gal-pager__btn" onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0} aria-label="Página anterior">
            ‹
          </button>
          {Array.from({ length: totalPages }).map((_, i) => (
            <button
              key={i}
              type="button"
              className={`rb-gal-pager__btn ${i === page ? 'is-active' : ''}`}
              onClick={() => setPage(i)}
            >
              {i + 1}
            </button>
          ))}
          <button
            type="button"
            className="rb-gal-pager__btn"
            onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
            disabled={page === totalPages - 1}
            aria-label="Próxima página"
          >
            ›
          </button>
        </div>
      )}
    </RebrandShell>
  );
}
